// 지금 소리가 걸린 대상 — **화면 밖으로 내보내는 자리 하나**
//
// ── 왜 이것이 필요했는가 ────────────────────────────────────────────────────
//
// 2026-08-15 결정으로 열람 화면이 보기 탭 둘이 됐다. 탭을 옮겨도 예약된 소리는 걷히지
// 않고, 정본은 그 대신 **defense 탭 상단에 그 소리가 누구 것인지와 개시까지 남은 시간을
// 한 줄로 적으라**고 한다 — 「이름 없는 카운트다운은 존재하지 않는다」의 소리 쪽 짝이다.
//
// 그런데 「무엇을 골랐는가」는 공격 열람 화면의 **지역 상태**이고, 신호 런타임이 들고 있는
// 대상에는 이름이 없다(`rallyId` 와 `openMs` 뿐이다). 그래서 그 값을 밖에서 읽을 길이
// 하나도 없었고, defense 탭은 **소리가 우는 동안 그 소리의 주인을 말하지 못했다.**
// 2026-08-16 대리 관측이 그것을 통과·불통 항목의 불통으로 잡았다 — 오디오 예약을 가로채
// 여섯 개가 살아 있는 것을 세어 놓고, 같은 화면 맨 아래가 「수비에는 소리 신호가
// 없습니다」라고 말하는 것을 함께 적었다.
//
// ── 왜 선택 상태를 껍데기로 올리지 않았는가 ─────────────────────────────────
//
// 올리면 공격 열람 화면의 상태 관리가 통째로 위로 옮겨 간다. 그 화면은 선택을 근거로 소리
// 예약·정렬·강조를 함께 굴리므로, 올리는 순간 **되돌릴 단위가 두 스텝에 걸친다.** 여기서
// 필요한 것은 「지금 무엇이 걸려 있는가」 한 값뿐이라, 그 한 값만 내보내는 문을 둔다.
//
// ── 왜 React 를 모르는가 ────────────────────────────────────────────────────
//
// `SignalRuntime` 이 평범한 클래스이고 `use-signal.ts` 가 그 위의 얇은 바인딩인 것과 같은
// 층 가르기다. 읽는 쪽은 `useSyncExternalStore` 로 붙이면 되고, 그러면 이 파일에 검사를
// 붙일 때 React 를 띄우지 않아도 된다.
//
// ── 스냅샷의 참조가 흔들리면 안 된다 ────────────────────────────────────────
//
// `useSyncExternalStore` 는 스냅샷을 **참조로** 견준다. 값이 같은데 새 객체를 돌려주면 매
// 렌더마다 「바뀌었다」로 읽혀 무한 렌더가 된다. 그래서 `publish` 가 값을 먼저 견주고
// **달라졌을 때만** 참조를 갈아 끼운다.

import type { OpenOutcome } from './open-outcome.ts'

/** 지금 소리가 걸린 rally 의 주인과 개시 시각. 고른 것이 없으면 `null`. */
export interface LiveSignalTarget {
  readonly rosterName: string
  /** 서버 시계 위의 개시 시각. 흐르는 숫자는 읽는 쪽이 자기 `now` 로 만든다. */
  readonly openMs: number
  /**
   * 사람이 소리를 껐는가. **껐다고 이 줄을 지우지 않는다** (정본 2026-09-02 개정).
   *
   * 지우면 「고를 대상이 없다」와 「내가 껐다」가 defense 탭에서 같은 화면이 된다. 둘은
   * 사람이 할 일이 다르다: 앞은 attack 탭에 가서 고르는 것이고 뒤는 손잡이를 다시 켜는
   * 것이다. 그리고 정본이 이 줄에 요구하는 것은 **누구 것인지와 개시까지 남은 시간**이라,
   * 껐다고 그 둘이 사라지는 것도 아니다. 소리만 안 날 뿐 예약의 주인은 그대로다.
   */
  readonly muted: boolean
  /**
   * 개시 순간에 일어난 일. **attack 화면이 런타임과 같은 시계로 정해 얼린 값이다**
   * (`open-outcome.ts`). 아직 안 정했거나 화면이 개시 순간을 못 봤으면 `null`.
   *
   * 읽는 쪽이 지금 끔 여부로 대신하지 않는 이유: 제때 울린 뒤 끈 사람에게 「꺼져 있었습니다」가
   * 뜨고, 개시 뒤에 고른 뒤 끈 사람에게도 같은 거짓이 뜬다 (2026-10-04 사용자 결정과 대리 관측).
   */
  readonly outcome: OpenOutcome | null
}

let current: LiveSignalTarget | null = null
const listeners = new Set<() => void>()

function same(a: LiveSignalTarget | null, b: LiveSignalTarget | null): boolean {
  if (a === null || b === null) return a === b
  // **`muted` 를 함께 본다.** 빠뜨리면 이름과 시각이 그대로인 채 껐다 켜기만 한 갱신이
  // 「값이 같다」로 걸러져, defense 탭이 계속 옛 문장을 말한다. 참조를 아껴 무한 렌더를
  // 막는 장치가 그대로 **사실을 막는 장치**가 되는 자리다.
  return (
    a.rosterName === b.rosterName &&
    a.openMs === b.openMs &&
    a.muted === b.muted &&
    a.outcome === b.outcome
  )
}

/**
 * 지금 걸린 대상을 알린다. **값이 달라졌을 때만 참조를 간다.**
 *
 * 부르는 자리는 공격 열람 화면 하나다. 둘이 되면 「지금 걸린 것」이 두 곳에서 정해져,
 * 마지막에 부른 쪽이 이긴다 — 그것은 규칙이 아니라 사고다.
 */
export function publishSignalTarget(next: LiveSignalTarget | null): void {
  if (same(current, next)) return
  current =
    next === null
      ? null
      : {
          rosterName: next.rosterName,
          openMs: next.openMs,
          muted: next.muted,
          outcome: next.outcome,
        }
  for (const listener of [...listeners]) listener()
}

/** `useSyncExternalStore` 가 쓰는 스냅샷. 값이 그대로면 **같은 참조**를 돌려준다. */
export function readSignalTarget(): LiveSignalTarget | null {
  return current
}

/** `useSyncExternalStore` 가 쓰는 구독. 돌려받는 함수를 부르면 뗀다. */
export function subscribeSignalTarget(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
